'use client';


import { useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { LoaderCircle, ShieldAlert } from 'lucide-react';

import { useAppAuth } from '@/hooks/use-app-auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ROUTES } from '@/lib/routes';

function GuardMessage({ title, description, ctaHref, ctaLabel }: { title: string; description: string; ctaHref: string; ctaLabel: string }) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center px-4 py-12">
      <Card className="w-full rounded-[2rem] border border-blush/70 bg-white shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3 text-terra">
            <ShieldAlert className="h-5 w-5" />
            <p className="text-xs font-semibold uppercase tracking-[0.24em]">Access control</p>
          </div>
          <CardTitle className="font-headline text-3xl text-charcoal">{title}</CardTitle>
          <CardDescription className="max-w-2xl text-base leading-7 text-charcoal/75">{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]">
            <Link href={ctaHref}>{ctaLabel}</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function LoadingGuard() {
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-3xl items-center justify-center px-4 py-12">
      <div className="flex items-center gap-3 rounded-full border border-blush/70 bg-white px-6 py-4 text-sm text-charcoal/75 shadow-sm">
        <LoaderCircle className="h-4 w-4 animate-spin text-terra" />
        Checking access…
      </div>
    </div>
  );
}

export function AuthGuard({
  children,
  mode,
  initialSession,
}: {
  children: ReactNode;
  mode: 'auth' | 'studio' | 'admin';
  initialSession?: {
    uid: string;
    onboardingCompleted: boolean;
    canAccessStudio: boolean;
    canAccessAdmin: boolean;
  } | null;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { firebaseReady, loading, session } = useAppAuth();
  const hasServerSession = Boolean(initialSession?.uid);
  const activeInitialSession = loading ? initialSession : null;
  const hasAnySession = Boolean(session?.uid || activeInitialSession?.uid);
  const canAccessStudioRoute = session?.canAccessStudio ?? activeInitialSession?.canAccessStudio ?? false;
  const canAccessAdminRoute = session?.canAccessAdmin ?? activeInitialSession?.canAccessAdmin ?? false;
  const onboardingCompleted = session?.onboardingCompleted ?? activeInitialSession?.onboardingCompleted ?? false;

  useEffect(() => {
    if (loading) return;
    if (!firebaseReady) return;

    const next = encodeURIComponent(pathname || '/');

    if (mode === 'studio' && !canAccessStudioRoute) {
      router.replace(ROUTES.luminaPress);
      return;
    }

    if (!hasAnySession) {
      router.replace(`${ROUTES.login}?next=${next}`);
      return;
    }

    if (mode === 'studio' && (!onboardingCompleted || !canAccessStudioRoute)) {
      router.replace(`${ROUTES.completeProfile}?next=${next}`);
      return;
    }

    if (mode === 'admin' && !canAccessAdminRoute) {
      router.replace(ROUTES.account);
    }
  }, [canAccessAdminRoute, canAccessStudioRoute, firebaseReady, hasAnySession, loading, mode, onboardingCompleted, pathname, router]);

  if (loading && !hasServerSession) return <LoadingGuard />;

  if (!firebaseReady) {
    return (
      <GuardMessage
        title="Firebase environment variables are still missing"
        description="Authentication is not available until the Firebase public environment values are configured for this deployment."
        ctaHref={ROUTES.contact}
        ctaLabel="Open contact page"
      />
    );
  }

  if (!hasAnySession) return <LoadingGuard />;

  if (mode === 'studio' && (!onboardingCompleted || !canAccessStudioRoute)) {
    return (
      <GuardMessage
        title="Lumina Press is coming soon"
        description="The studio is currently reserved for verified owner and admin testers."
        ctaHref={canAccessStudioRoute ? ROUTES.completeProfile : ROUTES.luminaPress}
        ctaLabel={canAccessStudioRoute ? "Complete profile" : "Discover Lumina Press"}
      />
    );
  }

  if (mode === 'admin' && !canAccessAdminRoute) {
    return (
      <GuardMessage
        title="Admin access is restricted"
        description="This account is authenticated, but it does not currently have the admin or owner role required to access the platform console."
        ctaHref={ROUTES.account}
        ctaLabel="Go to account"
      />
    );
  }

  return <>{children}</>;
}
